// [codex 2026-06-17] Official Default mirrors TP's active Desktop custom theme.

export interface ThemeConfig {
  name: string;
  label: string;
  primary?: string;
  secondary?: string;
  accent: string;
  bg: string;
  text: string;
  bullish: string;
  bearish: string;
  surface: string;
  border: string;
  muted: string;
  icon?: string;
  tiltScout?: string;
  severe?: string;
  neutralSevere?: string;
  neutral?: string;
  lowNeutral?: string;
  low?: string;
  special?: boolean;
  fontBody?: string;
  fontHeading?: string;
  fontMono?: string;
  glassEnabled?: boolean;
  /** @deprecated Legacy saved themes used frosted/liquid backdrop variants. */
  glassVariant?: "solid" | "frosted" | "liquid";
  borderRadius?: string;
  easeDefault?: string;
}

export const THEME_PRESETS: Record<string, ThemeConfig> = {
  default: {
    name: "default",
    label: "Default",
    accent: "#C79F4A",
    primary: "#C79F4A",
    secondary: "#8A681F",
    bg: "#080808",
    text: "#e0e0e0",
    bullish: "#C79F4A",
    bearish: "#663f3f",
    surface: "#0a0a0a",
    border: "#1a1a1a",
    muted: "#4a4a4a",
    severe: "#980c0c",
    neutralSevere: "#ff4d00",
    neutral: "#0b1800",
    lowNeutral: "#5b754d",
    low: "#b8af56",
    special: true,
    fontBody: "'Space Grotesk', sans-serif",
    fontHeading: "'Doto', monospace",
    fontMono: "'Space Mono', monospace",
    glassEnabled: true,
    borderRadius: "0.5rem",
    easeDefault: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  },
  "something-solvys": {
    name: "something-solvys",
    label: "Something Solvys",
    accent: "#c79f4a",
    primary: "#c79f4a",
    secondary: "#5a4630",
    bg: "#0d0c09",
    text: "#c38f25",
    bullish: "#d49616",
    bearish: "#824d4d",
    surface: "#151310",
    border: "#c79f4a",
    muted: "#6b6455",
    icon: "#c79f4a",
    tiltScout: "#c79f4a",
    severe: "#da0000",
    neutralSevere: "#ac5318",
    neutral: "#c79f4a",
    lowNeutral: "#526089",
    low: "#073c00",
    special: true,
    fontBody: "'Space Grotesk', sans-serif",
    fontHeading: "'Doto', monospace",
    fontMono: "'Space Mono', monospace",
    glassEnabled: true,
    borderRadius: "0.5rem",
    easeDefault: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  },
  "something-monochrome": {
    name: "something-monochrome",
    label: "Something Monochrome",
    accent: "#e0e0e0",
    primary: "#e0e0e0",
    secondary: "#737373",
    bg: "#000000",
    text: "#e0e0e0",
    bullish: "#e0e0e0",
    bearish: "#e0e0e0",
    surface: "#0a0a0a",
    border: "#1a1a1a",
    muted: "#4a4a4a",
    icon: "#e0e0e0",
    tiltScout: "#e0e0e0",
    special: true,
    fontBody: "'Space Grotesk', sans-serif",
    fontHeading: "'Doto', monospace",
    fontMono: "'Space Mono', monospace",
    glassEnabled: true,
    borderRadius: "0.5rem",
    easeDefault: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  },
};

export const ALL_PRESETS: Record<string, ThemeConfig> = THEME_PRESETS;

const STORAGE_KEY = "fintheon:theme";
const CUSTOM_STORAGE_KEY = "fintheon:theme-custom";
const PRESET_MATCH_KEYS: (keyof ThemeConfig)[] = [
  "accent",
  "primary",
  "secondary",
  "bg",
  "text",
  "bullish",
  "bearish",
  "surface",
  "border",
  "muted",
  "icon",
  "tiltScout",
  "severe",
  "neutralSevere",
  "neutral",
  "lowNeutral",
  "low",
  "glassEnabled",
];

export const DEFAULT_THEME = THEME_PRESETS.default;

export function normalizeThemeGlassConfig(theme: ThemeConfig): ThemeConfig {
  const legacyVariant = theme.glassVariant;
  const glassEnabled =
    legacyVariant === "solid" ? false : (theme.glassEnabled ?? true);
  const { glassVariant: _legacyGlassVariant, ...rest } = theme;
  return { ...rest, glassEnabled };
}

export function loadStoredTheme(): ThemeConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_THEME;

    if (stored === "frosted-glass" || stored === "liquid-glass") {
      return { ...DEFAULT_THEME, glassEnabled: true };
    }

    if (stored === "custom") {
      const custom = localStorage.getItem(CUSTOM_STORAGE_KEY);
      if (custom) return normalizeThemeGlassConfig(JSON.parse(custom));
      return DEFAULT_THEME;
    }

    if (ALL_PRESETS[stored])
      return normalizeThemeGlassConfig(ALL_PRESETS[stored]);
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function saveTheme(theme: ThemeConfig): void {
  try {
    const normalizedTheme = normalizeThemeGlassConfig(theme);
    const presetKey = Object.keys(ALL_PRESETS).find(
      (k) =>
        ALL_PRESETS[k].special === normalizedTheme.special &&
        PRESET_MATCH_KEYS.every(
          (key) => ALL_PRESETS[k][key] === normalizedTheme[key],
        ),
    );

    if (presetKey) {
      localStorage.setItem(STORAGE_KEY, presetKey);
      localStorage.removeItem(CUSTOM_STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, "custom");
      localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(normalizedTheme));
    }
  } catch {
    // localStorage unavailable
  }
}
